"use client";

import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  cn,
} from "@atlas/core";
import { cva } from "class-variance-authority";
import { useCallback, useEffect, useState } from "react";

const containerVariants = cva("flex w-full flex-col gap-4");
const mainImageVariants = cva(
  "aspect-video w-full overflow-hidden rounded-lg border border-border bg-muted/30",
);

const THUMB_SLIDES_PER_VIEW = {
  sm: 4,
  md: 5,
  lg: 6,
} as const;

interface IMediaGalleryProps {
  images: string[];
  className?: string;
  size?: "sm" | "md" | "lg";
}

export function MediaGallery({
  images,
  className,
  size = "md",
}: IMediaGalleryProps) {
  const [mainApi, setMainApi] = useState<CarouselApi | null>(null);
  const [thumbApi, setThumbApi] = useState<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const thumbSlidesPerView = THUMB_SLIDES_PER_VIEW[size];

  const onSelect = useCallback(() => {
    if (!mainApi) return;
    const index = mainApi.selectedScrollSnap();
    setSelectedIndex(index);
    thumbApi?.scrollTo(index);
  }, [mainApi, thumbApi]);

  const onThumbClick = useCallback(
    (index: number) => {
      mainApi?.scrollTo(index);
    },
    [mainApi],
  );

  useEffect(() => {
    setSelectedIndex((i) =>
      images.length ? Math.min(i, images.length - 1) : 0,
    );
  }, [images.length]);

  useEffect(() => {
    if (!mainApi) return;
    onSelect();
    mainApi.on("select", onSelect);
    mainApi.on("reInit", onSelect);
    return () => {
      mainApi.off("select", onSelect);
      mainApi.off("reInit", onSelect);
    };
  }, [mainApi, onSelect]);

  if (!images?.length) return null;

  return (
    <div className={cn(containerVariants(), className)}>
      <div className="relative w-full">
        <Carousel setApi={setMainApi} className="w-full">
          <CarouselContent>
            {images.map((src, index) => (
              <CarouselItem
                key={`${index}-${src.slice(0, 50)}`}
                className="h-auto! w-full! basis-full">
                <div className={cn(mainImageVariants())}>
                  <img
                    src={src}
                    alt=""
                    className="size-full object-cover"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {images.length > 1 && (
        <Carousel
          setApi={setThumbApi}
          opts={{
            dragFree: true,
          }}
          spacing="sm"
          slidesPerView={thumbSlidesPerView}
          className="w-full">
          <CarouselContent className="-ml-2 flex-row">
            {images.map((src, index) => (
              <CarouselItem
                key={`thumb-${index}-${src.slice(0, 50)}`}
                cardSize="none"
                className="cursor-pointer pl-2"
                role="button"
                tabIndex={0}
                aria-label={`View image ${index + 1}`}
                aria-pressed={selectedIndex === index}
                onClick={() => onThumbClick(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onThumbClick(index);
                  }
                }}>
                <div
                  className={cn(
                    "relative aspect-square overflow-hidden rounded-lg border-2 transition-all",
                    index === selectedIndex
                      ? "border-primary opacity-100"
                      : "border-transparent opacity-40 hover:opacity-70",
                  )}>
                  <img
                    src={src}
                    alt=""
                    className="size-full object-cover"
                    loading="lazy"
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}
    </div>
  );
}
